import {
  Box,
  Chip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import {
  buildPaymentCycleOption,
  getRangeKey,
  isPaymentCycleCalendarMonth,
} from '../utils/paymentCycleDateRanges'

export default function DateRangeSelector({
  dateRangeMode,
  setDateRangeMode,
  dialogStartDate,
  setDialogStartDate,
  customDateRange,
  setCustomDateRange,
  selectedPeriodRange,
  setSelectedPeriodRange,
  paymentConfig,
  periodOptions = [],
  isFetching,
  isValidDateRange,
}) {
  const usesCalendarMonthCycle = isPaymentCycleCalendarMonth(paymentConfig)
  const selectedOption = buildPaymentCycleOption(
    customDateRange?.[0],
    customDateRange?.[1],
    'Selected range',
  )
  const mergedPeriodOptions = [
    selectedOption,
    ...(periodOptions || []),
  ].reduce((acc, option) => {
    if (!option?.start || !option?.end) return acc
    const key = option.key || getRangeKey(option.start, option.end)
    if (acc.some((existing) => existing.key === key)) return acc
    acc.push({ ...option, key })
    return acc
  }, [])

  const customStartDate = customDateRange?.[0] ? dayjs(customDateRange[0]) : null
  const customEndDate = customDateRange?.[1] ? dayjs(customDateRange[1]) : null
  const customRangeInvalid =
    dateRangeMode === 'custom' &&
    customStartDate &&
    customEndDate &&
    customEndDate.isBefore(customStartDate, 'day')

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <strong className="mb-2 d-block">Date Range Mode</strong>
        <ToggleButtonGroup
          value={dateRangeMode}
          exclusive
          onChange={(event, newMode) => {
            if (newMode !== null) {
              setDateRangeMode(newMode)
            }
          }}
          aria-label="date range mode"
        >
          {usesCalendarMonthCycle ? (
            <ToggleButton value="monthly" aria-label="monthly">
              Month
            </ToggleButton>
          ) : (
            <ToggleButton value="period" aria-label="period">
              Period/Cycle
            </ToggleButton>
          )}
          <ToggleButton value="custom" aria-label="custom range">
            Custom Range
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      
      <div>
        <strong className="mb-2 d-block">
          {dateRangeMode === 'monthly'
            ? 'Select Month'
            : dateRangeMode === 'period'
              ? 'Select Period/Cycle'
              : 'Select Date Range'}
        </strong>
        {dateRangeMode === 'monthly' ? (
          <DatePicker
            value={dialogStartDate}
            views={['month', 'year']}
            onChange={(val) => setDialogStartDate(val)}
            sx={{ width: '100%' }}
          />
        ) : dateRangeMode === 'period' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {mergedPeriodOptions.map((option) => {
              const isSelected =
                selectedPeriodRange?.start?.isSame?.(option.start, 'day') &&
                selectedPeriodRange?.end?.isSame?.(option.end, 'day')
              return (
                <Chip
                  key={option.key}
                  label={option.label}
                  clickable
                  disabled={isFetching}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => setSelectedPeriodRange(option)}
                />
              )
            })}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <DatePicker
              label="From date"
              value={customStartDate}
              disabled={isFetching}
              onChange={(value) => setCustomDateRange([value, customDateRange?.[1] || value])}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
            <DatePicker
              label="To date"
              value={customEndDate}
              minDate={customStartDate || undefined}
              disabled={isFetching}
              onChange={(value) => setCustomDateRange([customDateRange?.[0] || value, value])}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Box>
        )}
        {customRangeInvalid && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            End date cannot be before start date
          </Typography>
        )}
        {!isValidDateRange && !customRangeInvalid && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Select a valid date range
          </Typography>
        )}
      </div>
    </div>
  )
}
